import { useState } from 'react';

import type { Dispute, ResolveDisputeRequest } from '@/api/disputes';
import type { DisputeStatus } from '@/api/generated/enums';
import { enterKeyScope, useAutoFocusFirstField } from '@/components/enterKeyNavigation';
import { Field, controlClass, textAreaClass } from '@/components/Field';
import { Button, Notice } from '@/components/PageHeading';
import { formatDateTime } from '@core/format/dates';
import { formatAmount } from '@core/format/money';
import { outcomeHints, parseAmount, resolveProblems, type ResolutionDraft } from './dispute';

const OUTCOMES: Exclude<DisputeStatus, 'Open'>[] = ['Upheld', 'Rejected', 'Withdrawn'];

/**
 * Closing an argument.
 *
 * <p>
 * <strong>Three ways out, and the screen says what each one means.</strong> "Rejected" and
 * "Withdrawn" are one click apart and record opposite things about who decided — a decision the
 * shop made and may be asked to defend, against a customer who went away. From the enum name alone
 * a clerk cannot tell which is which, and the log is unreadable a year later if they picked wrong.
 * </p>
 *
 * <p>
 * The settled figure is offered only on `Upheld`, because that is the only status where it means
 * anything: a rejected dispute settled at the original charge, and recording that would make the
 * two indistinguishable in a report.
 * </p>
 */
export function ResolveDisputeDrawer({
  dispute,
  saving,
  onCommit,
  onClose,
}: {
  dispute: Dispute;
  saving: boolean;
  onCommit: (payload: ResolveDisputeRequest) => void;
  onClose: () => void;
}) {
  const [draft, setDraft] = useState<ResolutionDraft>({
    status: 'Rejected',
    resolution: '',
    settledAmount: '',
  });

  const focusRef = useAutoFocusFirstField<HTMLDivElement>();

  const problems = resolveProblems(draft);

  return (
    <div className="fixed inset-0 z-9999 flex justify-end" role="dialog" aria-modal="true">
      <button
        type="button"
        aria-label="Close"
        className="absolute inset-0 cursor-default bg-[var(--overlay-scrim)]"
        onClick={onClose}
      />

      <div
        ref={focusRef}
        {...enterKeyScope}
        className="relative flex h-full w-full max-w-[560px] flex-col border-l border-[var(--overlay-border)] bg-[var(--overlay-surface)] shadow-lg"
      >
        <div className="border-b border-border-color p-4">
          <h2 className="text-base font-semibold text-gray-900">Close this dispute</h2>
          <p className="mt-0.5 text-[13px] text-default">
            {dispute.customerName}
            {dispute.bookingNumber && (
              <>
                {' · '}
                <span className="font-mono-nums">{dispute.bookingNumber}</span>
              </>
            )}
            {dispute.garmentName ? ` · ${dispute.garmentName}` : ''}
          </p>
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4">
          <div className="rounded-md border border-border-color p-3">
            <div className="text-[13px] text-default">What the customer said</div>
            <p className="mt-1 text-sm whitespace-pre-wrap text-gray-900">{dispute.claim}</p>
            <div className="mt-2 flex flex-wrap justify-between gap-3 text-[13px] text-default">
              <span>
                Raised {formatDateTime(dispute.raisedOn)}
                {dispute.raisedByName ? ` by ${dispute.raisedByName}` : ''}
              </span>
              {dispute.disputedAmount !== null && (
                <span className="font-mono-nums">
                  {formatAmount(dispute.disputedAmount)} in question
                </span>
              )}
            </div>
          </div>

          <div>
            <div className="mb-2 block text-sm font-medium text-dark">How does it end?</div>
            <div className="space-y-2">
              {OUTCOMES.map((status) => (
                <label
                  key={status}
                  className="flex cursor-pointer items-start gap-3 rounded-md border border-border-color p-3 hover:bg-light"
                >
                  <input
                    type="radio"
                    name="status"
                    className="mt-1"
                    checked={draft.status === status}
                    /*
                      Clearing the figure on the way out of Upheld rather than leaving it to be
                      refused on save: a clerk who typed 600 and then changed their mind about the
                      outcome meant to change the outcome, not to argue with a validation message.
                    */
                    onChange={() =>
                      setDraft({
                        ...draft,
                        status,
                        settledAmount: status === 'Upheld' ? draft.settledAmount : '',
                      })
                    }
                  />
                  <span>
                    <span className="block text-sm font-medium text-dark">{status}</span>
                    <span className="mt-0.5 block text-[13px] text-default">
                      {outcomeHints[status]}
                    </span>
                  </span>
                </label>
              ))}
            </div>
          </div>

          {draft.status === 'Upheld' && (
            <>
              <Field
                label="Settled at"
                hint="What the shop agreed to come down to. Leave it blank if the charge was dropped entirely and record that below."
              >
                <input
                  type="number"
                  min={0}
                  step="0.01"
                  className={controlClass}
                  value={draft.settledAmount}
                  onWheel={(event) => event.currentTarget.blur()}
                  onChange={(event) => setDraft({ ...draft, settledAmount: event.target.value })}
                />
              </Field>

              {/*
                The thing most likely to be misread here, and the service says so in its own
                remarks: upholding records what was agreed and moves no money. A shop that stopped
                at this drawer has told the customer one figure and billed them another.
              */}
              <Notice tone="warning" title="This does not change what the customer is charged">
                Correct the inspection as well, on the returns screen. Keeping the two apart is what
                preserves the record of what was originally claimed.
              </Notice>
            </>
          )}

          <Field
            label="Why it ended this way"
            hint="What the shop stands on if this comes back. “Rejected” on its own answers no question anybody will later ask of it."
            required
          >
            <textarea
              className={textAreaClass}
              rows={4}
              value={draft.resolution}
              onChange={(event) => setDraft({ ...draft, resolution: event.target.value })}
            />
          </Field>

          {problems.length > 0 && (
            <Notice tone="warning" title="Before this can be closed">
              <ul className="list-none space-y-1 ps-0">
                {problems.map((problem) => (
                  <li key={problem}>{problem}</li>
                ))}
              </ul>
            </Notice>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-border-color p-4">
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            icon="icon icon-badge-check"
            shortcut="save"
            data-enter-key-commit
            disabled={saving || problems.length > 0}
            onClick={() =>
              onCommit({
                status: draft.status,
                resolution: draft.resolution.trim(),
                settledAmount:
                  draft.status === 'Upheld' ? (parseAmount(draft.settledAmount) ?? null) : null,
              })
            }
          >
            Close as {draft.status.toLowerCase()}
          </Button>
        </div>
      </div>
    </div>
  );
}
